import type { Capability } from '@gridfit/core/principals'
import type { MeResponse } from '@/auth/session'

export interface NavItem {
  readonly path: string
  readonly label: string
  /**
   * Hidden without it — and the SERVER still enforces on every command and
   * query. Both halves are tested: a hidden item the server would allow is a
   * different bug from a visible item the server refuses.
   */
  readonly capability: Capability
}

/**
 * `/costs` and `/portal` (review round 1's Important 2, added after the
 * brief's original five): `landing.ts`'s `PRIORITY` table promises a
 * landing spot to every capability it names, including `costs-entry` and
 * `client-portal` — neither of which any of the original five items here
 * gated on, so a principal holding ONLY one of them landed on a real screen
 * with an EMPTY nav (`costs-entry`, on `/money`) or, worse, on a route that
 * did not exist in `routes.tsx` at all (`client-portal`, on `/portal` —
 * measured to crash into react-router's own dev error boundary). Fixed by
 * giving each its OWN area, not by widening Money's gate: architecture
 * §7.2 is explicit that `commercial` (margin visibility) is a capability
 * `costs-entry` (cost data entry) must NOT imply, so `capability:
 * 'costs-entry'` on the EXISTING Money item would have quietly reopened
 * the exact promise this whole nav filter exists to keep — a costs-entry
 * principal seeing a "Money" link is the identical bug class as an
 * ops-only principal seeing one. `test/shell.test.tsx`'s "every landing
 * capability has somewhere real to go" test is what now keeps this table
 * and `landing.ts`'s `PRIORITY` from drifting apart silently again.
 */
export const NAV_ITEMS = [
  { path: '/pipeline', label: 'Pipeline', capability: 'commercial' },
  { path: '/jobs', label: 'Jobs', capability: 'ops' },
  { path: '/site-ops', label: 'Site ops', capability: 'ops' },
  { path: '/money', label: 'Money', capability: 'commercial' },
  { path: '/costs', label: 'Costs', capability: 'costs-entry' },
  { path: '/admin', label: 'Admin', capability: 'user-admin' },
  { path: '/portal', label: 'Portal', capability: 'client-portal' },
] as const satisfies readonly NavItem[]

/**
 * The literal union of area paths — `'/pipeline' | '/jobs' | ...`, derived
 * from the table above rather than written out a second time. `as const
 * satisfies readonly NavItem[]` above is what makes it derivable: `satisfies`
 * still checks every row against `NavItem` (a typo in a `capability` is the
 * same compile error it always was), while `as const` keeps the literal
 * strings instead of widening them to `string`.
 *
 * Exists for `routes.tsx`'s `AREA_MODULES`, which is typed `Record<NavPath,
 * ...>` — so `tsc` refuses a nav row with no area module AND an area module
 * with no nav row, in the editor, before any test runs. That is the
 * compile-time half of web-pre-deploy item 6's "a new area cannot be added
 * without declaring the capability that gates it"; the runtime half (which a
 * type stripped by esbuild cannot provide) is
 * `tools/structural/web-area-routes.test.ts`. Both are kept: the type is the
 * faster feedback, the test is the one that still fails a mutation applied
 * with `tsc` not looking.
 */
export type NavPath = (typeof NAV_ITEMS)[number]['path']

export function visibleNav(me: MeResponse | undefined): readonly NavItem[] {
  if (!me) return []
  return NAV_ITEMS.filter((item) => me.capabilities.includes(item.capability))
}
